import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import type { JiraEpicSyncStatus } from '@/domain/models/development.js';

interface Props {
    status: JiraEpicSyncStatus;
    epicKey: string | null;
    epicUrl: string | null;
    epicStatusName: string | null;
    creatingLabel: string;
    retryLabel: string;
    variant: 'inline' | 'field';
    testId?: string;
}

function EpicKey({ epicKey, epicUrl }: { epicKey: string; epicUrl: string | null }) {
    if (epicUrl === null) {
        return <span>{epicKey}</span>;
    }
    return (
        <Link href={epicUrl} target="_blank" rel="noopener noreferrer" data-testid="jira-epic-status-link" sx={{ font: 'inherit', fontWeight: 'inherit' }}>
            {epicKey}
        </Link>
    );
}

function EpicProgress({ creatingLabel, retryLabel, status }: { creatingLabel: string; retryLabel: string; status: 'PENDING' | 'RETRYING' }) {
    if (status === 'RETRYING') {
        return <span>{retryLabel}</span>;
    }
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} data-testid="jira-epic-status-loader" />
            <span>{creatingLabel}</span>
        </Box>
    );
}

export function JiraEpicStatus({ status, epicKey, epicUrl, epicStatusName, creatingLabel, retryLabel, variant, testId = 'jira-epic-status' }: Props) {
    if (status === 'PENDING' || status === 'RETRYING') {
        const progress = <EpicProgress status={status} creatingLabel={creatingLabel} retryLabel={retryLabel} />;
        if (variant === 'inline') {
            return (
                <Typography component="span" variant="caption" color="text.secondary" data-testid={testId}>
                    {progress}
                </Typography>
            );
        }
        return (
            <Box component="span" data-testid={testId} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {progress}
            </Box>
        );
    }
    if (status === 'LINKED' && epicKey) {
        const key = <EpicKey epicKey={epicKey} epicUrl={epicUrl} />;
        if (variant === 'inline') {
            return (
                <Typography component="span" variant="caption" color="text.secondary" data-testid={testId}>
                    {key}
                    {epicStatusName ? ` · ${epicStatusName}` : ''}
                </Typography>
            );
        }
        return (
            <Box component="span" data-testid={testId} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {key}
            </Box>
        );
    }
    if (variant === 'field') {
        return (
            <Box component="span" data-testid={testId}>
                —
            </Box>
        );
    }
    return null;
}
